import Splide from '@splidejs/splide';
import imagesLoaded from 'imagesloaded';
import { gsap } from 'gsap';

export default class {
  constructor() {
    this.splide = null;
    this.isLoaded = false;
    this.mainVisual = '#js-mainVisual';
    this.loading = '#js-mainVisualLoading';
    this.content = '#js-mainVisualContent';
    this.slider = '#js-mainVisualSlider';
    this.sliderBg = '#js-mainVisualSliderBg';
    this.character = '.js-mv-character';
    this.label = '.js-mv-label';
    this.characterTl = null;
    this.sliderTl = null;
    this.interval = 4;
    this.badge = null;
  }

  init() {
    gsap.to(`${this.loading} img`, {
      rotation: 360,
      repeat: -1,
      ease: 'linear',
      duration: 1,
    });
    imagesLoaded(document.querySelector(this.mainVisual), () => {
      const tl = gsap.timeline();
      this.sliderInit();
      tl.set([this.content, this.slider, this.label, this.character], {
        opacity: 0,
      });
      tl.to(this.loading, {
        duration: 1,
        opacity: 0,
      });
      tl.call(() => {
        document.querySelector(this.loading).remove();
      });
      tl.to(this.content, {
        opacity: 1,
        duration: 1,
      });
      tl.to(this.slider, {
        opacity: 1,
        duration: 1,
      }, '-=1');

      this.badge = gsap.timeline();
      this.badge.to('.js-mv-label-bg', {
        rotation: 360,
        transformOrigin: 'center',
        repeat: -1,
        ease: 'linear',
        duration: 10,
      });
    });
  }

  sliderInit() {
    this.splide = new Splide(this.slider, {
      type: 'fade',
      rewind: true,
      autoplay: false,
      interval: 10000,
      arrows: false,
      pauseOnHover: false,
    });
    this.splide.on('mounted', () => {
      const items = document.querySelectorAll('.splide__pagination__page');
      items.forEach((item) => {
        item.insertAdjacentHTML('afterbegin', '<span class="splide__pagination__bar"></span>');
      });
    });
    this.splide.on('visible', (SlideComponent) => {
      const { index } = SlideComponent;
      this.changeColorClass(index);
      this.sliderTimeline(SlideComponent);
    });
    this.splide.on('moved', (newIndex, oldIndex, destIndex) => {
      const items = document.querySelectorAll('.splide__pagination__page');
      if (newIndex === 0 && oldIndex > newIndex) {
        items.forEach((item) => {
          item.classList.remove('-complete');
        });
      }
      if (destIndex > 0) {
        items.forEach((item, i) => {
          if (i < destIndex) {
            item.classList.add('-complete');
          }
        });
      }
    });
    this.splide.mount();
  }

  changeColorClass(sliderCount) {
    const nextSliderCount = (sliderCount === 4) ? 0 : sliderCount + 1;
    const mainVisual = document.querySelector(this.mainVisual);
    const sliderBg = document.querySelector(this.sliderBg);
    const sliderBgClass = [
      '-red',
      '-blue',
      '-green',
      '-yellow',
      '-pink',
    ];
    sliderBgClass.forEach((className) => {
      mainVisual.classList.remove(className);
      sliderBg.classList.remove(className);
    });
    mainVisual.classList.add(sliderBgClass[nextSliderCount]);
    sliderBg.classList.add(sliderBgClass[sliderCount]);
  }

  sliderTimeline(SlideComponent) {
    const loadingDelay = this.isLoaded ? 0 : 2;
    const character = SlideComponent.slide.querySelector('.js-mv-character');
    const characterImg = SlideComponent.slide.querySelectorAll('.js-mv-character .character__img');
    const label = SlideComponent.slide.querySelector('.js-mv-label');
    const durationPerFrame = 0.5;
    const animationTime = durationPerFrame * characterImg.length;
    this.interval = (animationTime >= 4) ? 6.5 : 6.5;

    if (this.sliderTl) {
      this.sliderTl.kill();
      gsap.set([character, label, this.sliderBg], { clearProps: 'all' });
    }

    if (!this.isLoaded) {
      this.isLoaded = true;
    }

    this.sliderTl = gsap.timeline();
    this.sliderTl.set(character, {
      x: 40,
      opacity: 0,
    });
    this.sliderTl.set(label, {
      scale: 0,
      opacity: 0,
    });
    this.sliderTl.set(this.sliderBg, {
      opacity: 1,
    });
    this.sliderTl.call(() => {
      this.characterAnimation(durationPerFrame, characterImg);
    });
    this.sliderTl.to(character, {
      delay: loadingDelay,
      x: 0,
      duration: 1,
      opacity: 1,
    });
    this.sliderTl.to(label, {
      opacity: 1,
      duration: 0.3,
      scale: 1,
    }, '-=0.5');
    this.sliderTl.to(this.sliderBg, {
      delay: this.interval,
      duration: 0.5,
      opacity: 0,
    });
    this.sliderTl.to(character, {
      x: -20,
      duration: 0.5,
      opacity: 0,
    }, '-=1');
    this.sliderTl.to(label, {
      opacity: 0,
      duration: 0.3,
      scale: 0,
    }, '-=1');
    this.sliderTl.call(() => {
      this.splide.go('+1');
    });

    const totalDuration = this.sliderTl.duration();
    let animationTimeline = gsap.getById('animationTimeline');
    const paginationBar = '.splide__pagination__page.is-active .splide__pagination__bar';
    if (animationTimeline) {
      animationTimeline.kill();
    } else {
      animationTimeline = gsap.timeline({
        id: 'animationTimeline',
        onComplete: () => {
          const item = document.querySelectorAll('.splide__pagination__page')[SlideComponent.index];
          item.classList.add('-complete');
        },
      });
    }
    animationTimeline.clear();
    animationTimeline.set(paginationBar, {
      scaleX: 0,
      transformOrigin: 'left',
    });
    animationTimeline.fromTo(paginationBar, { scaleX: 0 }, {
      scaleX: 1,
      duration: totalDuration,
      ease: 'none',
    });
  }

  characterAnimation(durationPerFrame, imgElements) {
    if (this.characterTl) {
      this.characterTl.kill();
      gsap.set(imgElements, { clearProps: 'all' });
    }

    gsap.set(imgElements, { opacity: 0 });
    gsap.set(imgElements[0], { opacity: 1 });

    this.characterTl = gsap.timeline({
      repeat: -1,
      repeatDelay: durationPerFrame,
    });

    imgElements.forEach((img, index, array) => {
      if (index < array.length - 1) {
        this.characterTl.set(img, {
          opacity: 0,
        }, (index + 1) * durationPerFrame);
      }
      if (index + 1 < array.length) {
        this.characterTl.set(array[index + 1], {
          opacity: 1,
        }, (index + 1) * durationPerFrame);
      } else {
        this.characterTl.set(img, {
          opacity: 0,
        }, 0);
        this.characterTl.set(array[0], {
          opacity: 1,
        }, 0);
      }
    });
  }
}
